import React, { createContext, ReactElement, ReactNode, useCallback, useContext, useMemo } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import routes from 'src/config/routes'
import { AvailyEvent } from '@customTypes/Availy'
import { analyticsEventModalExit } from 'src/services/analytics'
import { postParentMsg } from 'src/utils/postMessage'
import { AppMode } from '@customTypes/Generic'
import { useChoiceState } from './ChoiceContext'
import { useSurveyContext } from './SurveyContext'
import { PatientTypeOptions } from 'src/config/patientTypeOptions'
import { setOriginalClinicId, useConfig, useConfigDispatch } from './ConfigurationContext'

export type CloseAvailyContextProps = (() => void) | undefined

const CloseAvailyContext = createContext<CloseAvailyContextProps>(undefined)

type CloseAvailyProviderProps = {
  children: ReactNode
  onClose: () => void
  mode: AppMode
}

export const CloseAvailyProvider = ({ children, onClose, mode }: CloseAvailyProviderProps): ReactElement => {
  const history = useHistory()
  const {
    treatmentBundleTypeReferenceId,
    insuranceType,
    patientType,
    verifiedPatientData: { patientReferenceId },
  } = useChoiceState()
  const { clinicId } = useConfig()
  const { surveyCompleted, setSurveyCompleted, setShouldOpenSurvey } = useSurveyContext()
  const { pathname } = useLocation()
  const configDispatch = useConfigDispatch()

  // Open survey only when treatment and patient type is selected
  const shouldOpenSurvey = useMemo(() => {
    const isReturningPatient = patientType === PatientTypeOptions.OLD
    const canOpenSurvey =
      mode !== AppMode.SURVEY &&
      pathname !== routes.success &&
      !surveyCompleted &&
      !!treatmentBundleTypeReferenceId &&
      !!insuranceType &&
      !!patientType &&
      !!clinicId

    return isReturningPatient ? canOpenSurvey && !!patientReferenceId : canOpenSurvey
  }, [
    treatmentBundleTypeReferenceId,
    mode,
    surveyCompleted,
    pathname,
    insuranceType,
    patientType,
    patientReferenceId,
    clinicId,
  ])

  const closeAvaily = useCallback(() => {
    history.push(routes.home)
    setShouldOpenSurvey(shouldOpenSurvey)
    mode === AppMode.SURVEY && setSurveyCompleted(true)
    configDispatch(setOriginalClinicId(undefined))
    onClose()

    postParentMsg({ type: AvailyEvent.CLOSE_MODAL })
    analyticsEventModalExit()
  }, [onClose, history, shouldOpenSurvey, mode, setShouldOpenSurvey, setSurveyCompleted, configDispatch])

  return <CloseAvailyContext.Provider value={closeAvaily}>{children}</CloseAvailyContext.Provider>
}

export const useCloseAvailyContext = (): CloseAvailyContextProps => {
  return useContext(CloseAvailyContext)
}
